import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Sparkles } from 'lucide-react';
import {
  getCvRequestTemplateById,
  getCvRequestTemplatesForMode,
  getSuggestedTemplateId,
} from '../../config/cvRequestTemplates';

/**
 * Premium admin picker: one-click CQC-aligned reasons + free-text override.
 */
export default function CvRequestReasonPicker({
  mode = 'replacement',
  reason = '',
  onReasonChange,
  alignment = null,
  suggestedTemplateId = null,
  className = '',
}) {
  const templates = getCvRequestTemplatesForMode(mode);
  const suggestedId = suggestedTemplateId || getSuggestedTemplateId(alignment);
  const suggestedTemplate = suggestedId ? getCvRequestTemplateById(suggestedId) : null;

  const applyTemplate = (template) => {
    if (template?.reason && onReasonChange) {
      onReasonChange(template.reason);
    }
  };

  return (
    <div className={className}>
      {suggestedTemplate && (
        <div className="mb-3 rounded-lg border border-purple-200 bg-purple-50 p-3">
          <p className="text-xs font-medium text-purple-900 flex items-center gap-1.5">
            <Sparkles className="h-3.5 w-3.5" />
            Suggested from CV vs application review
          </p>
          <p className="mt-1 text-xs text-purple-800">{alignment?.summary}</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2 h-7 text-xs border-purple-300 text-purple-800 hover:bg-purple-100"
            onClick={() => applyTemplate(suggestedTemplate)}
            data-testid="cv-request-suggested-template"
          >
            Use: {suggestedTemplate.label}
          </Button>
        </div>
      )}

      <Label className="text-sm font-medium">Quick reasons</Label>
      <p className="text-xs text-slate-500 mt-0.5 mb-2">
        Tap a template — the applicant sees this in their dashboard. Edit before sending if needed.
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {templates.map((template) => (
          <Badge
            key={template.id}
            variant="outline"
            className={`cursor-pointer text-xs py-1 px-2 hover:bg-slate-100 ${
              suggestedId === template.id ? 'border-purple-300 bg-purple-50 text-purple-800' : ''
            }`}
            onClick={() => applyTemplate(template)}
            data-testid={`cv-template-${template.id}`}
          >
            {template.label}
          </Badge>
        ))}
      </div>

      <Label className="text-sm font-medium">Message to applicant</Label>
      <Textarea
        value={reason}
        onChange={(e) => onReasonChange?.(e.target.value)}
        className="mt-2 min-h-[100px]"
        placeholder="Select a template above or write a clear, professional reason (min. 10 characters)."
        data-testid="cv-request-reason-textarea"
      />
      <p className="text-xs text-slate-500 mt-1">
        CV must match the employment history on the application form — both are checked during recruitment.
      </p>
    </div>
  );
}